import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Close12, Search16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { CloseCircle, DropdownV2, JustifyVariant, SingleIconSelect, Switch, SwitchSize } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { getPersistedSearchConfig, useGridSearchConfig } from '@/hooks/useGridSearchConfig'
import { colTypeToLabel } from '@/shared/ag-grid'
import { EVENT_KEYS } from '@/shared/custom-event'
import { appIconKeys } from '@/shared/icons'
import { DEFAULT_DEBOUNCE_MS } from '@/shared/makyo-hooks/constants'
import { ColumnTypeIcon } from '@/components/ColumnTypeIcon'

import { useGridV2Context } from '../contexts/GridV2Context'

export const GridSearchV2 = memo(() => {
  const { t } = useTranslation()
  const { tableId, activeTable, searchConfig, updateSearchConfig } = useGridV2Context()
  const prevTableIdRef = useRef(tableId)

  // get columns types existed in current table
  const tableColumnsTypes = useMemo(() => {
    const columnSet = new Set()
    activeTable?.settings._Columns.forEach((col) => {
      columnSet.add(col._Type)
    })
    return Array.from(columnSet)
  }, [activeTable])

  const [show, setShow] = useState(false)
  const [search, setSearch] = useState(searchConfig.value)

  const ref = useRef(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // show search
    const handleSearchToggle = () => {
      setShow((prev) => {
        if (!Boolean(prev)) {
          timeoutRef.current = setTimeout(() => {
            inputRef.current?.focus()
          }, 100)
        }
        return !prev
      })
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // listen to find key on ctrl + f (windows) or cmd + f (mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        handleSearchToggle()
      }

      // close on esc key
      if (e.key === 'Escape') {
        setShow(false)
      }
    }

    window.addEventListener(EVENT_KEYS.GRID_SEARCH_TOGGLE, handleSearchToggle)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener(EVENT_KEYS.GRID_SEARCH_TOGGLE, handleSearchToggle)
      window.removeEventListener('keydown', handleKeyDown)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (prevTableIdRef.current === tableId) return

    prevTableIdRef.current = tableId

    setShow(false)

    const timer = setTimeout(() => {
      const persistedConfig = getPersistedSearchConfig(tableId)
      if (persistedConfig.value || persistedConfig.columnTypes.length > 0) {
        setShow(true)
        setSearch(persistedConfig.value)
      } else {
        setSearch('')
      }
    }, 200)

    return () => clearTimeout(timer)
    // 
  }, [tableId])

  const debouncedChangeSearchConfig = useCallback(
    (value: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        updateSearchConfig((prev) => ({ ...prev, value }))
      }, DEFAULT_DEBOUNCE_MS)
    },
    [updateSearchConfig]
  )

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value)
    debouncedChangeSearchConfig(e.currentTarget.value)
  }

  const handleClearSearch = () => {
    setSearch('')
    debouncedChangeSearchConfig('')
  }

  return (
    <div
      ref={ref}
      className={cn(
        'w-[350px] fixed right-4 top-[110px] z-200 border bdr',
        'bg-zinc-100 dark:bg-slate-800 rounded transition-all ease-in-out duration-150',
        show ? 'visible translate-x-0' : 'invisible translate-x-full'
      )}
    >
      <div className='px-3 py-2.5 border-b bdr flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <MaterialIcon icon='tune' size={18} /> <Trans>Search Columns & Rows</Trans>
        </div>
        <div className='cursor-pointer p-1' onClick={() => setShow(false)}>
          <Close12 width={10} height={10} />
        </div>
      </div>

      <div className='p-4 space-y-4'>
        <div className='flex items-center gap-2 bg-zinc-200 dark:bg-slate-900 rounded-sm overflow-hidden p-2'>
          <Search16 width={15} height={15} className='text-light' />
          <input
            ref={inputRef}
            type='text'
            className='flex-1'
            placeholder={`Type to search ${searchConfig.type === 'column' ? 'columns' : 'rows'}`}
            value={search}
            onChange={handleChangeSearch}
          />
          <button
            type='button'
            title='Clear'
            className={cn(
              'cursor-pointer hover:text-red-500 rounded-full text-light',
              search ? 'visible' : 'invisible'
            )}
            onClick={() => {
              handleClearSearch()
            }}
          >
            <CloseCircle />
          </button>
        </div>

        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Trans>Search by:</Trans>
          </div>

          <div>
            <SingleIconSelect
              options={[
                {
                  id: 'column',
                  title: t('Columns'),
                  icon: <MaterialIcon icon={appIconKeys.tableColumn} size={18} />,
                },
                {
                  id: 'row',
                  title: t('Rows'),
                  icon: <MaterialIcon icon={appIconKeys.tableRow} size={18} />,
                },
              ]}
              selected={searchConfig.type}
              onClick={(opt) => {
                updateSearchConfig((prev) => ({
                  ...prev,
                  type: opt.id as 'column' | 'row',
                  ...(opt.id === 'row' && { columnTypes: [] }),
                }))

                inputRef.current?.focus()
              }}
            />
          </div>
        </div>

        {searchConfig.type === 'column' && (
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Trans>Filter by:</Trans>
            </div>

            <div className='flex items-center gap-2'>
              <DropdownV2>
                <DropdownV2.Trigger asChild>
                  <div className='cursor-pointer border border-brand-500 text-brand-500 rounded-sm px-3 py-1.5 flex items-center gap-4'>
                    <div className='flex items-center gap-2'>
                      <MaterialIcon icon={appIconKeys.tableColumn} size={16} />
                      <span className='font-semibold'>
                        {searchConfig.columnTypes.length > 0
                          ? `${searchConfig.columnTypes.length} Selected`
                          : 'Column Types'}
                      </span>
                    </div>

                    {searchConfig.columnTypes.length > 0 && (
                      <button
                        type='button'
                        title='Clear'
                        className={cn(
                          'cursor-pointer hover:text-red-500 rounded-full text-light',
                          searchConfig.columnTypes.length > 0 ? 'visible' : 'invisible'
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          updateSearchConfig((prev) => ({ ...prev, columnTypes: [] }))
                        }}
                      >
                        <CloseCircle />
                      </button>
                    )}
                  </div>
                </DropdownV2.Trigger>

                <DropdownV2.Content
                  showArrow
                  className='z-302 w-48 text-m-m p-2'
                  align='end'
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  {tableColumnsTypes.map((type: any) => {
                    const isSelected = searchConfig.columnTypes.includes(type)
                    return (
                      <div
                        key={type}
                        className='p-2 cursor-pointer flex items-center justify-between hover:bg-zinc-200 dark:hover:bg-slate-700'
                        onClick={() => {
                          updateSearchConfig((prev) => {
                            if (prev.columnTypes.includes(type)) {
                              return { ...prev, columnTypes: prev.columnTypes.filter((t) => t !== type) }
                            }
                            return { ...prev, columnTypes: [...prev.columnTypes, type] }
                          })
                        }}
                      >
                        <div className='flex-1 flex items-center gap-2'>
                          <ColumnTypeIcon type={type} />
                          <span>{colTypeToLabel(type)}</span>
                        </div>

                        <div className='shrink-0'>
                          <Switch
                            checked={isSelected}
                            justify={JustifyVariant.Start}
                            size={SwitchSize.XS}
                            onClick={() => {
                              updateSearchConfig((prev) => {
                                if (prev.columnTypes.includes(type)) {
                                  return { ...prev, columnTypes: prev.columnTypes.filter((t) => t !== type) }
                                }
                                return { ...prev, columnTypes: [...prev.columnTypes, type] }
                              })
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </DropdownV2.Content>
              </DropdownV2>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

GridSearchV2.displayName = 'GridSearchV2'
